import AsyncStorage from '@react-native-async-storage/async-storage';
import { doc, getDoc, serverTimestamp, setDoc, Timestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { logAnalyticsEvent } from '@/services/analyticsLogger';

const SESSION_THROTTLE_MS = 60 * 60 * 1000;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

type SessionSource = 'cold_start' | 'foreground';

type TrackRetentionSessionOptions = {
  userId: string;
  source: SessionSource;
  route?: string;
  hasCompletedOnboarding: boolean;
};

const sessionThrottleKey = (userId: string) => `@tripvio_retention_session_last_${userId}`;

const toDate = (value: unknown): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (value instanceof Timestamp) return value.toDate();
  if (typeof value === 'object' && typeof (value as { toDate?: unknown }).toDate === 'function') {
    try {
      return (value as { toDate: () => Date }).toDate();
    } catch {
      return null;
    }
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
};

const daysSince = (date: Date | null, now: number): number | undefined => {
  if (!date) return undefined;
  return Math.max(0, Math.floor((now - date.getTime()) / MS_PER_DAY));
};

const shouldTrackSession = async (userId: string, now: number): Promise<boolean> => {
  try {
    const raw = await AsyncStorage.getItem(sessionThrottleKey(userId));
    const lastTrackedAt = raw ? Number(raw) : 0;
    return !Number.isFinite(lastTrackedAt) || now - lastTrackedAt >= SESSION_THROTTLE_MS;
  } catch {
    return true;
  }
};

const markSessionTracked = async (userId: string, now: number): Promise<void> => {
  try {
    await AsyncStorage.setItem(sessionThrottleKey(userId), String(now));
  } catch {
    // ignore local throttle persistence failures
  }
};

export async function trackRetentionSession({
  userId,
  source,
  route,
  hasCompletedOnboarding,
}: TrackRetentionSessionOptions): Promise<void> {
  const now = Date.now();
  if (!(await shouldTrackSession(userId, now))) {
    return;
  }

  const userRef = doc(db, 'users', userId);
  let createdAt: Date | null = null;
  let lastActiveAt: Date | null = null;

  try {
    const snapshot = await getDoc(userRef);
    if (snapshot.exists()) {
      const data = snapshot.data();
      createdAt = toDate(data.createdAt);
      lastActiveAt = toDate(data.lastActiveAt);
    }
  } catch {
    // Keep the session write best-effort even if the context read fails.
  }

  const payload = {
    source,
    route,
    daysSinceSignup: daysSince(createdAt, now),
    daysSinceLastActive: daysSince(lastActiveAt, now),
    hasCompletedOnboarding,
  };

  void logAnalyticsEvent('app_open', payload);
  void logAnalyticsEvent('session_start', payload);

  try {
    await setDoc(
      userRef,
      {
        lastActiveAt: serverTimestamp(),
        lastSeenRoute: route ?? null,
      },
      { merge: true },
    );
    await markSessionTracked(userId, now);
  } catch {
    // Analytics and re-engagement should never block app startup.
  }
}
